import dayjs from 'dayjs';
import axiosInstance, { jsonHeaders } from '@/api/axiosInstance';
import { environment } from '@/config/environment';
import type { SchedulingItem } from '@/types';
import type { SortFilterOption, SortFilterValue } from '@/components/shared/SortFilterDialog';

export type SchedulingTab = 'TRUCK' | 'MCT';

export const SCHEDULING_TABS: SchedulingTab[] = ['TRUCK', 'MCT'];
export const SCHEDULING_PAGE_SIZE_OPTIONS = [25, 50, 100];
export const MAX_EXTRACT_RECORDS = 5000;
export const NOTE_REQUIRED_STATUSES = ['MOT-Recycle', 'MOT-Reject'];
export const UPLOAD_SUCCESS_STATUS = 'Success';

const MODE_OF_TRANSPORTATION: Record<SchedulingTab, string> = { TRUCK: 'Truck', MCT: 'MCT' };

const EXPORT_ORDER_STATUSES = [
  'IM-Confirmed',
  'MOT-Pending Carrier',
  'MOT-Recycle',
  'MOT-Reject',
  'MOT-Scheduled',
  'MOT-Scheduled/Updated',
];

export interface ScheduleFormat {
  tab: SchedulingTab;
  format: string;
  label: string;
}

export const IMPORT_FORMATS: ScheduleFormat[] = [
  { tab: 'TRUCK', format: 'Generic', label: 'Truck - Generic' },
  { tab: 'TRUCK', format: 'Ryder', label: 'Truck - Ryder' },
  { tab: 'MCT', format: 'MCT', label: 'MCT' },
  { tab: 'MCT', format: 'Generic', label: 'Truck - Generic' },
];

export const EXPORT_FORMATS: ScheduleFormat[] = [
  { tab: 'TRUCK', format: 'GENERIC', label: 'Truck - Generic' },
  { tab: 'TRUCK', format: 'RYDER', label: 'Truck - Ryder' },
  { tab: 'MCT', format: 'SCHEDULING_MCT', label: 'MCT' },
  { tab: 'MCT', format: 'GENERIC', label: 'Truck - Generic' },
];

export interface FilterDefinition extends SortFilterOption {
  column: string;
  operator: 'contains' | 'greaterThanOrEqual' | 'lessThanOrEqual';
  isDate?: boolean;
}

const textFilter = (column: string, label: string): FilterDefinition => ({
  property: column,
  label,
  column,
  operator: 'contains',
});

// The list endpoint binds one value per column, so dates are filtered as Min / Max bounds.
const dateFilters = (column: string, label: string): FilterDefinition[] => [
  { property: `${column}Min`, label: `${label} - Min`, column, operator: 'greaterThanOrEqual', isDate: true },
  { property: `${column}Max`, label: `${label} - Max`, column, operator: 'lessThanOrEqual', isDate: true },
];

export const FILTER_DEFINITIONS: FilterDefinition[] = [
  textFilter('OrderNumber', 'Order #'),
  ...dateFilters('OrderPlacedDate', 'Order Creation Date'),
  textFilter('OrderCreator', 'Order Creator'),
  textFilter('OrderType', 'Order Type'),
  textFilter('ShippingCondition', 'Ship Cond.'),
  textFilter('OrderStatus', 'Order Status'),
  textFilter('ShipToPlantCode', 'Ship-to Code'),
  textFilter('PlantCode', 'Supply Point Code'),
  textFilter('ItemNumber', 'Item'),
  textFilter('MaterialCode', 'Material'),
  textFilter('RouteCode', 'Route Code'),
  textFilter('RouteDescription', 'Route Name'),
  textFilter('Package', 'Package'),
  textFilter('UnitOfMeasure', 'Unit Of Measure'),
  textFilter('SalesUnitOfMeasure', 'Order UoM'),
  ...dateFilters('PlannedLoadDate', 'Planned Load Date'),
  ...dateFilters('RequestedDeliveryDate', 'Requested Delivery Date'),
  textFilter('DeliveryNumber', 'Delivery Number'),
  textFilter('ShipmentStatus', 'Shipment Status'),
  ...dateFilters('LoadDate', 'Load Date'),
  ...dateFilters('DeliveryDate', 'Delivery Date'),
  textFilter('DeliveryUnitOfMeasure', 'Delivery Unit Of Measure'),
  textFilter('GrossWeight', 'Gross Weight'),
  textFilter('Carrier', 'Carrier'),
  textFilter('TruckNumber', 'Truck Number'),
  textFilter('RailCar', 'Railcar ID'),
  textFilter('VesselNumber', 'Vessel Number'),
  textFilter('ShippingConditionDescription', 'Shipping Condition Description'),
  textFilter('MaterialDescription', 'Material Description'),
  textFilter('HazMat', 'Hazmat'),
  textFilter('ShipToDescription', 'Ship-to Name'),
  textFilter('ShipToRegion', 'Ship-to State/Prov'),
  textFilter('ShipToCity', 'Ship-to City'),
  textFilter('ShipToPostalCode', 'Ship-to Postal Code'),
  textFilter('ShipToCountry', 'Ship-to Country'),
  textFilter('PlantDescription', 'Supply Point Name'),
  textFilter('PlantRegion', 'Supply Point State/Prov'),
  textFilter('PlantCity', 'Supply Point City'),
  textFilter('PlantPostalCode', 'Supply Point Postal Code'),
  textFilter('PlantCountry', 'Supply Point Country'),
  textFilter('CustFreightForwarderId', 'Freight Forwarder ID'),
  textFilter('CustDrcCodeId', 'Drc Code ID'),
  textFilter('CustDrcCodeCountry', 'Drc Code Country'),
  textFilter('CustDrcCodeDesc', 'Drc Code Description'),
  textFilter('CustDrcCodeRegion', 'Drc Code Region'),
  textFilter('SellingSalesOrg', 'Selling Sales Org.'),
  textFilter('TgrAdr', 'TgrAdr'),
];

const byLabel = (left: SortFilterOption, right: SortFilterOption) => left.label.localeCompare(right.label);

const FILTER_DEFINITIONS_BY_PROPERTY = new Map(FILTER_DEFINITIONS.map((definition) => [definition.property, definition]));

export const SCHEDULING_FILTER_FIELDS: SortFilterOption[] = FILTER_DEFINITIONS
  .map(({ property, label }) => ({ property, label }))
  .sort(byLabel);

export const SCHEDULING_SORT_FIELDS: SortFilterOption[] = [
  { property: 'DeliveryDate', label: 'Delivery Date' },
  { property: 'DeliveryNumber', label: 'Delivery Number' },
  { property: 'ItemNumber', label: 'Item Number' },
  { property: 'LoadDate', label: 'Load Date' },
  { property: 'MaterialCode', label: 'Material' },
  { property: 'MaterialDescription', label: 'Material Description' },
  { property: 'ModeOfTransportation', label: 'Mode Of Transportation' },
  { property: 'OrderStatus', label: 'Order Status' },
  { property: 'PlantCity', label: 'Plant City' },
  { property: 'PlantCode', label: 'Supply Point Code' },
  { property: 'PlantCountry', label: 'Supply Point Country' },
  { property: 'PlantDescription', label: 'Supply Point Name' },
  { property: 'PlantPostalCode', label: 'Supply Point Postal Code' },
  { property: 'PlantRegion', label: 'Supply Point State/Prov' },
  { property: 'PlannedLoadDate', label: 'Planned Load Date' },
  { property: 'RequestedDeliveryDate', label: 'Requested Delivery Date' },
  { property: 'ShipmentStatus', label: 'Shipment Status' },
  { property: 'ShippingConditionDescription', label: 'Shipping Condition Description' },
  { property: 'ShipToCity', label: 'Ship-to City' },
  { property: 'ShipToCountry', label: 'Ship-to Country' },
  { property: 'ShipToDescription', label: 'Ship-to Name' },
  { property: 'ShipToPlantCode', label: 'Ship-to Code' },
  { property: 'ShipToPostalCode', label: 'Ship-to Postal Code' },
  { property: 'ShipToRegion', label: 'Ship-to State/Prov' },
].sort(byLabel);

const DEFAULT_SORTS = [
  { ColumnName: 'PlannedLoadDate', Value: 'ASC' },
  { ColumnName: 'OrderNumber', Value: 'ASC' },
  { ColumnName: 'ItemNumber', Value: 'ASC' },
];

export const toIsoDate = (value: string) => {
  const parsed = dayjs(value.trim());
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
};

export const getModeOfTransportation = (tab: SchedulingTab) => MODE_OF_TRANSPORTATION[tab];

export const isStatusDisabled = (status: string) => !/MOT-/.test(status);

export const isHazMat = (value: SchedulingItem['hazMat']) =>
  value === true || ['X', 'Y', 'TRUE', '1'].includes(String(value ?? '').trim().toUpperCase());

export interface SchedulingListInput {
  tab: SchedulingTab;
  page: number;
  pageSize: number;
  sorts: SortFilterValue[];
  filters: SortFilterValue[];
}

export interface SchedulingListRequest {
  params: Record<string, unknown>;
  ignoredFilters: SortFilterValue[];
}

export const buildSchedulingListRequest = ({
  tab,
  page,
  pageSize,
  sorts,
  filters,
}: SchedulingListInput): SchedulingListRequest => {
  const params: Record<string, unknown> = {
    PageNumber: page,
    PageSize: pageSize,
    ModeOfTransportation: getModeOfTransportation(tab),
    ModeOfTransportationFilter: 'equals',
    Sorting: sorts.length > 0
      ? sorts.map((sort) => ({ ColumnName: sort.property, Value: sort.value }))
      : DEFAULT_SORTS,
  };
  const ignoredFilters: SortFilterValue[] = [];

  filters.forEach((filter) => {
    const definition = FILTER_DEFINITIONS_BY_PROPERTY.get(filter.property);
    const value = definition?.isDate ? toIsoDate(filter.value) : filter.value.trim();

    if (!definition || !value || definition.column in params) {
      ignoredFilters.push(filter);
      return;
    }

    params[definition.column] = value;
    params[`${definition.column}Filter`] = definition.operator;
  });

  return { params, ignoredFilters };
};

export interface SchedulingListResult {
  items: SchedulingItem[];
  total: number;
}

export const fetchScheduling = async (params: Record<string, unknown>): Promise<SchedulingListResult> => {
  const { data } = await axiosInstance.get('/scheduling', { params, headers: jsonHeaders() });
  const items = (data?.datas ?? data?.data ?? []) as SchedulingItem[];

  return { items, total: Number(data?.totalCount ?? data?.count ?? items.length) };
};

export const fetchShipmentStatuses = async (): Promise<string[]> => {
  const { data } = await axiosInstance.get('/cyberorderstatus', { headers: jsonHeaders() });
  const rows: unknown[] = Array.isArray(data) ? data : (data?.datas ?? data?.data ?? []);
  const statuses = rows.map((row) => {
    if (typeof row === 'string') return row;
    const record = row as { orderStatus?: string; cyberOrderStatus?: string };
    return record?.orderStatus ?? record?.cyberOrderStatus ?? '';
  });

  return Array.from(new Set(statuses.map((status) => status.trim()).filter(Boolean)));
};

export const submitDeliveryStatus = async (item: SchedulingItem, status: string, source: string) => {
  await axiosInstance.post(
    '/delivery/update',
    [{
      orderNumber: item.orderNumber,
      deliveryNumber: item.deliveryNumber,
      itemNumber: item.itemNumber,
      deliveryStatus: status,
      source,
    }],
    { headers: jsonHeaders() },
  );
};

export const addOrderNote = async (orderNumber: string, text: string) => {
  await axiosInstance.patch('/note', { orderNumber, text }, { headers: jsonHeaders() });
};

export interface DownloadScheduleInput {
  tab: SchedulingTab;
  format: string;
  source: string;
  userId: string;
  filters: SortFilterValue[];
}

export const downloadSchedule = async ({ tab, format, source, userId, filters }: DownloadScheduleInput) => {
  const columnFilters = filters.flatMap((filter) => {
    const definition = FILTER_DEFINITIONS_BY_PROPERTY.get(filter.property);
    const value = definition?.isDate ? toIsoDate(filter.value) : filter.value.trim();

    return definition && value ? [{ ColumnName: filter.property, Value: value }] : [];
  });

  const { data } = await axiosInstance.post(
    '/download',
    {
      UserId: userId,
      ProductType: null,
      Source: source,
      RecordId: null,
      SearchFilters: '',
      FormatType: format,
      ModeOfTransportation: [getModeOfTransportation(tab)],
      OrderStatus: EXPORT_ORDER_STATUSES,
      LinesPerPage: MAX_EXTRACT_RECORDS,
      PageNumber: 1,
      Filters: columnFilters,
      Permission: true,
    },
    { headers: jsonHeaders(), responseType: 'blob' },
  );

  return {
    blob: data instanceof Blob ? data : new Blob([data]),
    filename: `${tab.toLowerCase()}_${format.toLowerCase()}_${dayjs().format('YYYYMMDD_HHmmss')}.csv`,
  };
};

// With responseType 'blob' error bodies arrive as a Blob, so the text has to be read back out.
export const readDownloadError = async (error: unknown): Promise<string> => {
  if ((error as { code?: string })?.code === 'ECONNABORTED') {
    return 'The export took too long and timed out. Narrow the filters and try again.';
  }

  const data = (error as { response?: { data?: unknown } })?.response?.data;

  if (data instanceof Blob) {
    const text = (await data.text()).trim();
    return text.replace(/^"|"$/g, '') || 'Export failed.';
  }

  return typeof data === 'string' && data.trim() ? data : 'Export failed.';
};

export interface UploadValidationError {
  row: number;
  error: string;
}

export interface ScheduleUploadResult {
  status?: string;
  rowCount?: number;
  errors?: UploadValidationError[];
  errorMessage?: string;
  fileName: string;
}

const toBase64 = (bytes: Uint8Array) => {
  const chunkSize = 0x8000;
  let binary = '';

  for (let index = 0; index < bytes.length; index += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(index, index + chunkSize));
  }

  return window.btoa(binary);
};

export const uploadScheduleFile = async (
  file: File,
  format: string,
  source: string,
): Promise<ScheduleUploadResult> => {
  const fileName = file.name;

  try {
    const data = toBase64(new Uint8Array(await file.arrayBuffer()));
    const { data: response } = await axiosInstance.post(
      '/upload/file',
      { FormatType: format.toUpperCase(), Data: data, Source: source, Permission: true },
      { headers: jsonHeaders(), timeout: environment.uploadTimeout },
    );

    return { ...response, fileName };
  } catch (error) {
    const response = (error as { response?: { data?: unknown; statusText?: string } })?.response;
    const body = response?.data as Partial<ScheduleUploadResult> | string | undefined;

    if (body && typeof body === 'object' && Array.isArray(body.errors)) {
      return { ...body, fileName };
    }

    const message = typeof body === 'string' && body.trim()
      ? body
      : response?.statusText || (error as Error)?.message || 'Upload failed';

    return { errorMessage: message, fileName };
  }
};
